import { HTTP_METHOD } from "@/constants/enums";
import { NEWS_PAGE, NEWS_USER_ENGAGEMENT } from "@api/apiRoutes";
import callAPIFromClient from "./api";

// Client Side API Definitions
export const fetchNewsFeed = async ({
    url = NEWS_PAGE,
    q = "",
    sortBy,
    sortOrder,
    categories,
    limit = 10,
    continuation,
    handleLogout,
}: {
    url?: string;
    q?: string;
    sortBy?: string;
    sortOrder?: string;
    categories?: string;
    limit?: number;
    continuation?: string;
    handleLogout?: Function;
}) => {
    // Create the query object with only defined values
    const query: Record<string, string | number> = {
        ...(continuation && { continuation }),
        ...(q && { q }),
        ...(sortBy && { sortBy }),
        ...(sortOrder && { sortOrder }),
        ...(categories && { categories }),
        ...(limit && { limit }),
    };

    const storedToken = await fetchAuthToken();
    let headers: Record<string, string> = {};

    if (storedToken) {
        try {
            headers = { Authorization: `Bearer ${storedToken}` };
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }

    try {
        const response = await callAPIFromClient({
            url,
            method: HTTP_METHOD.GET,
            queryParams: query,
            headers,
            isHeaderInclude: true,
        });

        if (response.statusCode === 401) {
            handleLogout && (await handleLogout({ skipLogoutApi: true }));
        }
        return response;
    } catch (error) {
        console.error("Error fetching data:", error);
        throw error; // Ensure errors are properly propagated
    }
};

export const fetchNewsCategories = async ({ url }: { url: string }) => {
    return await callAPIFromClient({ url: url, method: HTTP_METHOD.GET });
};

export const fetchAuthToken = async () => {
    const response = await fetch("/api/auth/get-auth-token");
    const data = await response.json();
    return data.authToken;
};

// Usage example

export const updateUserEngagement = async ({
    value,
    engagementType,
    slug,
    handleLogout,
}: {
    value?: number | string;
    engagementType: string;
    slug?: string;
    handleLogout?: Function;
}) => {
    const storedToken = await fetchAuthToken();

    let headers: Record<string, string> = {};

    if (storedToken) {
        try {
            headers = { Authorization: `Bearer ${storedToken}` };
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }

    try {
        const response = await callAPIFromClient({
            url: `${NEWS_USER_ENGAGEMENT}/${slug}/engagement`,
            method: HTTP_METHOD.POST,
            headers,

            body: {
                engagementType, // Include engagementType
                payload: {
                    value: Number(value), // Include value in the payload
                },
                isHeaderInclude: true,
            },
        });

        if (response.statusCode === 401) {
            handleLogout &&
                handleLogout({ skipLogoutApi: true, retriggerAuth: true });
        }

        return response;
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

export const fetchUserEngagementRelatedToComments = async ({
    slug,
    body,
}: {
    slug: string;
    body: {
        engagementType: string;
        value: string | number;
        commentId?: string;
    };
}) => {
    const storedToken = await fetchAuthToken();
    let headers: Record<string, string> = {};

    if (storedToken) {
        try {
            headers = { Authorization: `Bearer ${storedToken}` };
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }

    try {
        const payload = {
            value: body?.value,
            commentId: body?.commentId,
        };

        const response = await callAPIFromClient({
            url: `${NEWS_USER_ENGAGEMENT}/${slug}/engagement`,
            method: HTTP_METHOD.POST,
            headers,
            body: {
                engagementType: body?.engagementType,
                payload,
            },
            isHeaderInclude: true,
        });

        return response;
    } catch (error) {
        console.error("Error fetching data:", error);
    }
};

export const fetchComment = async ({
    slug,
    commentId,
    offset,
    limit,
}: {
    slug: string;
    commentId?: string;
    offset?: number;
    limit?: number;
}) => {
    const storedToken = await fetchAuthToken();
    let headers: Record<string, string> = {};

    if (storedToken) {
        try {
            headers = { Authorization: `Bearer ${storedToken}` };
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }

    const query: Record<string, any> = {};

    if (commentId) {
        query.commentId = commentId;
    }
    if (offset) {
        query.offset = offset;
    }
    if (limit) {
        query.limit = limit;
    }
    const url = `${NEWS_USER_ENGAGEMENT}/${slug}/comments`;

    try {
        const response = await callAPIFromClient({
            url: url,
            method: HTTP_METHOD.GET,
            queryParams: query,
            headers,
            isHeaderInclude: true,
        });

        return response;
    } catch (error) {
        console.error("Error fetching comments:", error);
    }
};

export const updateNewsImage = async ({
  slug,
  formData,
  username,
  password
}: {
  slug: string;
  formData: FormData;
  username: string; 
  password: string;
}) => {

    const storedToken = await fetchAuthToken();
    let headers: Record<string, string> = {
        "x-user-name": username,
        "x-user-password": password,
    };
    if (storedToken) {
        try {
            headers["Authorization"] = `Bearer ${storedToken}`;
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }
     try {

        const response = await callAPIFromClient({
            url: `${NEWS_USER_ENGAGEMENT}/${slug}/update/image`,
            method: HTTP_METHOD.POST,
            headers,
            body: formData
        });
        return response;
    } catch (error) {
        console.error("Error updating news event ids:", error);
    }
}

export const updateNewsExternalEventIds = async ({
    slug,
    externalEventIds,
    actionType,
    username,
    password
    
}: {
    slug: string;
    externalEventIds: number[];
    actionType: string;
    username: string;
    password: string;
}) => {

    const storedToken = await fetchAuthToken();
    let headers: Record<string, string> = {
        "x-user-name": username,
        "x-user-password": password,
    };
    if (storedToken) {
        try {
            headers["Authorization"] = `Bearer ${storedToken}`;
        } catch (err) {
            console.error("Error parsing auth token:", err);
        }
    }
    try {
        const response = await callAPIFromClient({
            url: `${NEWS_USER_ENGAGEMENT}/${slug}/update/external-ids?actionType=${actionType}`,
            method: HTTP_METHOD.POST,
            headers,
            body: {
                externalEventIds,
            },
        });
        return response;
    } catch (error) {
        console.error("Error updating news image", error);
    }
}